// components/features/entreprises/EntrepriseRow.tsx

import { Entreprise } from "../../../types/features/Entreprise";
import TableRow from "../../ui/TableRow";

interface EntrepriseRowProps {
  entreprise: Entreprise;
  onAction: (entreprise: Entreprise) => void;
  onSelect?: (entreprise: Entreprise, isSelected: boolean) => void;
  isSelected?: boolean;
  selectable?: boolean;
}

export default function EntrepriseRow({
  entreprise,
  onAction,
  onSelect,
  isSelected = false,
  selectable = false
}: EntrepriseRowProps) {
  const name = entreprise.name?.trim();
  
  // Nom de l'entreprise ou statut "Nouvelle entreprise"
  const entrepriseName = name || "Nouvelle entreprise";

  // Email ou statut "Nouvelle entreprise"
  const emailText = entreprise.support_email ?? "Nouvelle entreprise";

  // Initiales ou icône "?"
  const initials = name
    ? name.substring(0, 2).toUpperCase()
    : "?";

  // Statut de l'abonnement avec couleur
  const getSubscriptionStatusColor = (status: string) => {
    const colors = {
      active: "bg-green-100 text-green-800",
      inactive: "bg-gray-100 text-gray-800",
      suspended: "bg-red-100 text-red-800",
      trial: "bg-blue-100 text-blue-800"
    };
    return colors[status as keyof typeof colors] || "bg-gray-100 text-gray-800";
  };

  // Plan d'abonnement
  const getSubscriptionPlanLabel = (plan: string) => {
    const plans = {
      starter: "Démarrage",
      professional: "Professionnel",
      enterprise: "Entreprise"
    };
    return plans[plan as keyof typeof plans] || plan;
  };

  return (
    <TableRow
      item={entreprise}
      onAction={onAction}
      onSelect={onSelect}
      isSelected={isSelected}
      selectable={selectable}
      actionable={true}
    >
      {/* Colonne Entreprise : Logo + Nom + Email */}
      <td className="py-3 px-4">
        <div className="flex items-center space-x-3">
          {/* Avatar JustCall style */}
          <div className={`h-9 w-9 rounded-full flex items-center justify-center font-semibold text-xs shrink-0 border ${
            entrepriseName === "Nouvelle entreprise"
              ? "bg-amber-50 text-amber-700 border-amber-200"
              : "bg-purple-100 text-purple-700 border-purple-200"
          }`}>
            {entreprise.logo ? (
              <img src={entreprise.logo} alt={entrepriseName} className="h-9 w-9 rounded-full object-cover" />
            ) : (
              initials
            )}
          </div>

          {/* Informations Nom & Email */}
          <div className="min-w-0 flex-1">
            <div className="flex items-center space-x-2">
              <span className={`text-sm block truncate ${
                entrepriseName === "Nouvelle entreprise" 
                  ? "font-semibold text-amber-600" 
                  : "font-medium text-gray-900"
              }`}>
                {entrepriseName}
              </span>
              
              {entrepriseName === "Nouvelle entreprise" && (
                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-100 text-amber-800">
                  Nouvelle
                </span>
              )}
            </div>

            <span className={`text-xs block truncate max-w-[200px] sm:max-w-[300px] ${
              emailText === "Nouvelle entreprise" ? "text-amber-500 italic" : "text-gray-500"
            }`}>
              {emailText}
            </span>
          </div>
        </div>
      </td>

      {/* Colonne Abonnement */}
      <td className="hidden sm:table-cell py-3 px-4 whitespace-nowrap">
        <div className="flex flex-col space-y-0.5">
          <span className="text-gray-700 text-sm font-medium">
            {getSubscriptionPlanLabel(entreprise.subscription_plan)}
          </span>
          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ${getSubscriptionStatusColor(entreprise.subscription_status)}`}>
            {entreprise.subscription_status.charAt(0).toUpperCase() + entreprise.subscription_status.slice(1)}
          </span>
        </div>
      </td>

      {/* Colonne Téléphone */}
      <td className="hidden md:table-cell py-3 px-4 whitespace-nowrap">
        <span className="text-gray-700 text-sm">
          {entreprise.support_phone ?? "—"}
        </span>
      </td>

      {/* Colonne Pays */}
      <td className="hidden lg:table-cell py-3 px-4 whitespace-nowrap">
        <span className="text-gray-500 text-xs">
          {entreprise.pays ?? "—"}
        </span>
      </td>

      {/* Colonne Date de création */}
      <td className="hidden xl:table-cell py-3 px-4 whitespace-nowrap">
        <span className="text-gray-500 text-xs">
          {entreprise.created_at
            ? new Date(entreprise.created_at).toLocaleDateString("fr-FR", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric"
              })
            : "—"}
        </span>
      </td>
    </TableRow>
  );
}